import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, Printer } from "lucide-react";
import { PageHeader, Container } from "@/components/shared/PageHeader";
import { PriceTable } from "@/components/shared/PriceTable";
import { StatusBadge } from "@/components/shared/Status";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { fetchRooms, fetchContent, fetchSite } from "@/lib/api";
import { floorLabel, paragraphs, fmtPrice } from "@/lib/format";
import { Seo } from "@/components/shared/Seo";

export default function Prices() {
  const { data: rooms, isLoading } = useQuery({ queryKey: ["rooms"], queryFn: fetchRooms });
  const { data: content } = useQuery({ queryKey: ["content"], queryFn: fetchContent });
  const { data: site } = useQuery({ queryKey: ["site"], queryFn: fetchSite });
  const info = content?.booking_info;
  const email = site?.contact_email || "post@halsnoykulturhus.no";

  return (
    <div data-testid="prices-page">
      <Seo title="Prisar" description={`Husleigesatsar for Halsnøy Kulturhus pr. ${site?.price_list_date || "13.03.2026"}: amfi, foajeen, kafé, stovene og møterom. Tydelege prisar for møte, konsertar, bryllaup, konfirmasjon og minnestund.`} />
      <PageHeader eyebrow="Husleigesatsar" title="Prisar du kan stole på – for kvart rom i huset" intro={`Prisliste pr. ${site?.price_list_date || "13.03.2026"}. Dersom ikkje anna er merka, er alle leigesatsar eksklusive vask, tilrigging og rydding.`}>
        <div className="mt-8 flex flex-wrap gap-3 print:hidden">
          <Button asChild size="lg" data-testid="prices-booking-link"><Link to="/leig-rom">Send bookingførespurnad <ArrowUpRight className="h-4 w-4" /></Link></Button>
          <Button variant="outline" size="lg" onClick={() => window.print()} data-testid="prices-print-button"><Printer className="h-4 w-4" /> Skriv ut</Button>
        </div>
      </PageHeader>

      {isLoading && <Container className="py-12"><Skeleton className="h-64 rounded-[12px]" /></Container>}

      {[1, 2, 3].map((f) => {
        const list = (rooms || []).filter((r) => r.floor === f);
        if (!list.length) return null;
        return (
          <section key={f} data-testid={`prices-floor-${f}`}>
            <Container className="grid gap-8 py-8 sm:py-10 lg:grid-cols-12 lg:gap-8">
              <div className="lg:col-span-3">
                <h2 className="t-h2">{floorLabel(f)}</h2>
                <p className="mt-2 text-sm text-muted-foreground">{list.map((r) => r.short_name || r.name).join(" · ")}</p>
              </div>
              <div className="grid gap-x-8 gap-y-12 lg:col-span-9 xl:grid-cols-2">
                {list.map((room) => (
                  <div key={room.id} id={room.slug} className="scroll-mt-28" data-testid={`prices-room-${room.slug}`}>
                    <div className="mb-2 flex flex-wrap items-end justify-between gap-2 border-b border-border pb-4">
                      <div>
                        <h3 className="t-h3"><Link to={`/rom/${room.slug}`} className="u-hover">{room.name}</Link></h3>
                        {room.capacity_note && <p className="mt-1 text-sm text-muted-foreground">{room.capacity_note}</p>}
                      </div>
                      {room.status === "closed" && <StatusBadge status="closed" />}
                    </div>
                    <PriceTable lines={room.price_lines} note={room.price_lines?.length ? null : room.price_note} testId={`price-table-${room.slug}`} />
                  </div>
                ))}
              </div>
            </Container>
          </section>
        );
      })}

      <Container className="grid gap-12 py-10 sm:py-12 lg:grid-cols-12 lg:gap-8 lg:py-16">
        <div className="lg:col-span-3"><h2 className="t-h2">Tillegg og tenester</h2></div>
        <div className="lg:col-span-5">
          <ul className="border-t border-border">
            <li className="grid grid-cols-[1fr_auto] items-baseline gap-4 rule-b py-3.5 text-[15px]"><span>Vaskehjelp (om du ikkje vaskar sjølv)</span><span className="tabular">{fmtPrice(site?.cleaning_help_price || 450)} <span className="text-muted-foreground">/ time</span></span></li>
            <li className="grid grid-cols-[1fr_auto] items-baseline gap-4 rule-b py-3.5 text-[15px]"><span>Teknikar lys og lyd (amfi)</span><span className="tabular">{fmtPrice(500)} <span className="text-muted-foreground">/ time</span></span></li>
            <li className="grid grid-cols-[1fr_auto] items-baseline gap-4 rule-b py-3.5 text-[15px]"><span>Heile huset</span><span className="text-muted-foreground">Etter avtale</span></li>
          </ul>
          <p className="mt-5 text-sm text-muted-foreground">Spørsmål om prisar? Skriv til <a className="font-medium text-foreground u-link" href={`mailto:${email}`}>{email}</a>.</p>
        </div>
        {info && (
          <div className="lg:col-span-4" data-testid="prices-cleaning-note">
            <h3 className="t-h3">{info.title}</h3>
            <div className="prose-nn mt-4 text-[15px] leading-relaxed text-muted-foreground">{paragraphs(info.body).map((p, i) => <p key={i}>{p}</p>)}</div>
          </div>
        )}
      </Container>
    </div>
  );
}
